import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Shirt } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { rosterSections } from "./nav-config";
import type { View } from "./nav-config";

export function RosterNavItem({
  activeView,
  onSelect,
  badge = 0,
  mobile = false,
}: {
  activeView: View;
  onSelect: (view: View) => void;
  // Giocatori senza nome EA da collegare (vedi Rosa > riquadro "senza EA").
  badge?: number;
  // Su mobile lo sheet di navigazione si chiude quando navighi (per design)
  // e l'accordion non sarebbe più visibile. Con mobile=true il click sul
  // titolo fa solo toggle dell'accordion, mai naviga; la navigazione
  // avviene esclusivamente cliccando una sotto-voce.
  mobile?: boolean;
}) {
  const { t } = useTranslation("shell");
  const isOnRoster = activeView === "players" || activeView === "compare-players" || activeView === "top11" || activeView === "archetypes" || activeView === "player";
  const [expanded, setExpanded] = useState(isOnRoster || mobile);

  useEffect(() => {
    if (isOnRoster) setExpanded(true);
  }, [isOnRoster]);

  return (
    <div>
      <button
        onClick={() => {
          if (mobile || isOnRoster) setExpanded((v) => !v);
          else onSelect("players");
        }}
        className={cn(
          "relative w-full flex items-center gap-3 h-10 px-3 rounded-xl text-left transition-colors",
          isOnRoster
            ? "nav-rail-pill font-semibold"
            : "text-muted-foreground hover:bg-raised hover:text-foreground",
        )}
      >
        <Shirt className="h-[18px] w-[18px]" />
        <span className="flex-1">{t("nav.players")}</span>
        {badge > 0 && (
          <span
            className="rounded-full bg-amber-500 text-white font-mono text-[10px] font-semibold px-1.5 py-0.5 leading-none tabular-nums"
            title={t("nav.rosterBadgeTitle", { count: badge })}
          >
            {badge}
          </span>
        )}
        <ChevronDown
          className={cn("h-3.5 w-3.5 transition-transform", expanded ? "" : "-rotate-90")}
        />
        {isOnRoster && <span className="nav-rail" aria-hidden />}
      </button>

      {expanded && (
        <div className="mt-px ml-[1.0625rem] pl-2.5 border-l border-border/60 flex flex-col gap-px">
          {rosterSections.map((section) => {
            const isActive = activeView === section.view;
            return (
              <button
                key={section.id}
                onClick={() => onSelect(section.view)}
                className={cn(
                  "px-2.5 py-1 rounded-md text-[12.5px] text-left transition-colors",
                  isActive
                    ? "bg-muted/70 text-foreground font-medium"
                    : "text-muted-foreground hover:bg-raised hover:text-foreground",
                )}
              >
                {t(section.labelKey)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
